import React, { useRef, useEffect } from 'react';
import thinkingSoundUrl from './soundThink2.mp3';

// Ported from chatbot-embeded/src/components/sections/AnimationSpeack.tsx (the 2D
// Fibonacci-sphere particle orb used by the embedded chat's call mode). Two local
// adaptations: the canvas fills its (square) container instead of a fixed 350 px box
// — the internal render resolution stays 350 so the animation itself is identical —
// and the thinking chime sits behind `enableThinkingSound` (default off: this page
// renders two orbs, and the user orb stays in 'think' for the whole answer, which
// would loop the chime).

interface AnimationSpeackProps {
  mediaStream: MediaStream | null;
  status: 'idle' | 'think' | 'client' | 'bot';
  isMuted: boolean;
  rmsLevel?: number;
  enableThinkingSound?: boolean;
  animationPending?: boolean;
  clouds?: any;
}

type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking';

function mapStatus(s: string): OrbState {
  if (s === 'client') return 'listening';
  if (s === 'bot') return 'speaking';
  if (s === 'think') return 'thinking';
  return 'idle'; // 'idle' and any unknown value
}

const AnimationSpeack: React.FC<AnimationSpeackProps> = ({ mediaStream, status, isMuted, rmsLevel, enableThinkingSound = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<OrbState>(mapStatus(status));
  const audioInputRef = useRef(0);
  const smoothLevelRef = useRef(0);
  const thinkingAudioRef = useRef<HTMLAudioElement | null>(null);
  const thinkingActiveRef = useRef(false);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Before first user interaction, show idle (not thinking) at stream open
  const hasInteractedRef = useRef(false);
  // RMS-based exit from speaking: wait until audio actually stops
  const speakingRmsCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lazy-init thinking audio with manual loop (2s gap between plays)
  if (enableThinkingSound && !thinkingAudioRef.current && typeof Audio !== 'undefined') {
    const a = new Audio(thinkingSoundUrl);
    a.addEventListener('ended', () => {
      if (!thinkingActiveRef.current) return;
      thinkingTimeoutRef.current = setTimeout(() => {
        if (!thinkingActiveRef.current) return;
        const audio = thinkingAudioRef.current;
        if (audio) {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      }, 2000);
    });
    thinkingAudioRef.current = a;
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      thinkingActiveRef.current = false;
      if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current);
      const a = thinkingAudioRef.current;
      if (a) {
        a.pause();
        thinkingAudioRef.current = null;
      }
      if (speakingRmsCheckRef.current) clearInterval(speakingRmsCheckRef.current);
    };
  }, []);

  // Central state applier — tracks previous state, controls thinking sound
  const applyState = (s: OrbState) => {
    const prev = stateRef.current;

    // Before first interaction, 'thinking' shows as 'idle' (no thinking sound at stream open)
    const effective = !hasInteractedRef.current && s === 'thinking' ? 'idle' : s;
    stateRef.current = effective;

    if (effective === 'listening' || effective === 'speaking') {
      hasInteractedRef.current = true;
    }

    const audio = thinkingAudioRef.current;
    // Thinking sound plays ONLY when transitioning from listening (user just finished talking)
    if (effective === 'thinking' && prev === 'listening') {
      thinkingActiveRef.current = true;
      if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = setTimeout(() => {
        if (!thinkingActiveRef.current) return;
        const a = thinkingAudioRef.current;
        if (a) {
          a.currentTime = 0;
          a.play().catch(() => {});
        }
      }, 2000);
    } else {
      thinkingActiveRef.current = false;
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
  };

  // State transition logic
  useEffect(() => {
    const newState = mapStatus(status);
    const current = stateRef.current;

    // Cancel any in-flight RMS check on every status change
    if (speakingRmsCheckRef.current) {
      clearInterval(speakingRmsCheckRef.current);
      speakingRmsCheckRef.current = null;
    }

    if (current === 'speaking' && newState === 'thinking') {
      // Stay in speaking until rmsLevel actually drops — prevents flickering
      // while bot audio is still buffered in the player after the stream ends
      speakingRmsCheckRef.current = setInterval(() => {
        if (audioInputRef.current < 0.02) {
          clearInterval(speakingRmsCheckRef.current!);
          speakingRmsCheckRef.current = null;
          applyState('thinking');
        }
      }, 120);
      return;
    }

    applyState(newState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Sync rmsLevel → audioInputRef
  useEffect(() => {
    if (typeof rmsLevel === 'number') {
      audioInputRef.current = isMuted ? 0 : Math.min(1, Math.max(0, rmsLevel * 35));
    }
  }, [rmsLevel, isMuted]);

  // Internal mic analyser (fallback when rmsLevel not provided)
  useEffect(() => {
    if (typeof rmsLevel === 'number') return;

    let audioContext: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let rafId: number | null = null;
    let disposed = false;

    const init = async () => {
      if (!mediaStream || isMuted || status !== 'client') {
        audioInputRef.current = 0;
        return;
      }
      try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 32;
        source = audioContext.createMediaStreamSource(mediaStream);
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (disposed || isMuted || status !== 'client') {
            audioInputRef.current = 0;
            return;
          }
          analyser.getByteFrequencyData(data);
          audioInputRef.current = data.reduce((a, b) => a + b, 0) / data.length / 100;
          rafId = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        console.error('Mic error:', e);
      }
    };

    init();
    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (source) {
        try {
          source.disconnect();
        } catch (_) {}
      }
      if (audioContext && audioContext.state !== 'closed') audioContext.close().catch(() => {});
    };
  }, [mediaStream, status, isMuted, rmsLevel]);

  // Main canvas animation loop — runs once, reads live values through refs
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = 350,
      H = 350,
      CX = W / 2,
      CY = H / 2;
    const FOCAL = 700;

    // Safe radii: CX + R*FOCAL/(FOCAL-R) < W  →  R < 140 with FOCAL=700,CX=175
    const R_IDLE = 75; // idle: small calm sphere
    const R_ACTIVE = 122; // think / listening / speaking: full size

    // Fibonacci sphere points
    const N = 520;
    const golden = Math.PI * (3 - Math.sqrt(5));
    const pts: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i < N; i++) {
      const yp = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(1 - yp * yp);
      const theta = golden * i;
      pts.push({ x: Math.cos(theta) * r, y: yp, z: Math.sin(theta) * r });
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const mix3 = (c1: number[], c2: number[], t: number) => [
      Math.round(lerp(c1[0], c2[0], t)),
      Math.round(lerp(c1[1], c2[1], t)),
      Math.round(lerp(c1[2], c2[2], t))
    ];

    const CFG: Record<OrbState, { base: number[]; light: number[]; accent: number[]; dim: number[]; spin: number; glowHue: string }> = {
      idle: {
        base: [130, 135, 145],
        light: [160, 165, 175],
        accent: [120, 125, 135],
        dim: [200, 205, 210],
        spin: 0.1,
        glowHue: '135,138,148'
      },
      listening: {
        base: [60, 120, 210],
        light: [20, 110, 235],
        accent: [0, 140, 255],
        dim: [150, 180, 220],
        spin: 0.3,
        glowHue: '40,130,255'
      },
      speaking: {
        base: [110, 70, 200],
        light: [120, 55, 225],
        accent: [150, 70, 255],
        dim: [185, 165, 225],
        spin: 0.45,
        glowHue: '120,55,225'
      },
      thinking: {
        base: [105, 110, 120],
        light: [140, 145, 155],
        accent: [95, 100, 110],
        dim: [180, 185, 195],
        spin: 0.2,
        glowHue: '110,113,123'
      }
    };

    type Pulse = { ax: number; ay: number; az: number; t: number; speed: number };
    const pulses: Pulse[] = [];
    const spawnPulse = (speed: number) => {
      pulses.push({ ax: Math.random() * 2 - 1, ay: Math.random() * 2 - 1, az: Math.random() * 2 - 1, t: -1.2, speed });
    };

    let t = 0;
    let lastPulse = 0;
    let radius = R_IDLE;
    let rafId: number;
    let disposed = false;

    const frame = (now: number) => {
      if (disposed) return;
      t += 0.016;
      const state = stateRef.current;
      const cfg = CFG[state];

      const targetLevel = state === 'listening' || state === 'speaking' ? audioInputRef.current : 0;
      smoothLevelRef.current = lerp(smoothLevelRef.current, targetLevel, 0.18);
      const sl = smoothLevelRef.current;

      // idle: small calm sphere  |  thinking/listening/speaking: full size
      const targetR = state === 'idle' ? R_IDLE : R_ACTIVE;
      radius = lerp(radius, targetR, 0.055);
      const R = radius;

      const ry = t * cfg.spin;
      const rx = Math.sin(t * 0.25) * 0.3 + 0.25;
      const cosY = Math.cos(ry),
        sinY = Math.sin(ry);
      const cosX = Math.cos(rx),
        sinX = Math.sin(rx);

      if (state === 'speaking' && now - lastPulse > 1800) {
        spawnPulse(0.55);
        lastPulse = now;
      }
      if (state === 'thinking' && now - lastPulse > 3500) {
        spawnPulse(0.28);
        lastPulse = now;
      }
      for (const p of pulses) p.t += 0.016 * p.speed;
      while (pulses.length && pulses[0].t > 2.4) pulses.shift();

      let breathe: number;
      if (state === 'idle') breathe = 1 + Math.sin(t * 1.1) * 0.018;
      else if (state === 'listening') breathe = 1 + sl * 0.07 + Math.sin(t * 2.0) * 0.012;
      else if (state === 'speaking') breathe = 1 + sl * 0.08 + Math.sin(t * 3.0) * 0.016;
      /* thinking */ else breathe = 1 + Math.sin(t * 1.9) * 0.028;

      ctx.clearRect(0, 0, W, H);

      const gAlpha = state === 'idle' ? 0.04 : state === 'speaking' ? 0.09 : 0.06;
      const glow = ctx.createRadialGradient(CX, CY, 15, CX, CY, R * 2.1);
      glow.addColorStop(0, `rgba(${cfg.glowHue},${gAlpha})`);
      glow.addColorStop(0.4, `rgba(${cfg.glowHue},${gAlpha * 0.3})`);
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);

      type Dot = { px: number; py: number; scale: number; sz: number; activation: number; depth: number };
      const rendered: Dot[] = [];

      for (const p of pts) {
        let x = p.x * cosY - p.z * sinY;
        let z = p.x * sinY + p.z * cosY;
        let y = p.y;
        const y2 = y * cosX - z * sinX;
        const z2 = y * sinX + z * cosX;
        y = y2;
        z = z2;

        const sx = x * R * breathe,
          sy = y * R * breathe,
          sz = z * R * breathe;
        const scale = FOCAL / (FOCAL + sz);
        const px = CX + sx * scale,
          py = CY + sy * scale;

        let activation = 0;
        for (const pulse of pulses) {
          const len = Math.hypot(pulse.ax, pulse.ay, pulse.az) || 1;
          const proj = (p.x * pulse.ax + p.y * pulse.ay + p.z * pulse.az) / len;
          const a = Math.max(0, 1 - Math.abs(proj - (pulse.t - 1.0)) * 10);
          if (a > activation) activation = a;
        }

        rendered.push({ px, py, scale, sz, activation, depth: (sz / R + 1) / 2 });
      }

      rendered.sort((a, b) => a.sz - b.sz);

      for (const r of rendered) {
        const d = r.depth;
        let col = mix3(CFG[state].dim, CFG[state].base, d);
        col = mix3(col, CFG[state].light, d * d);
        if (r.activation > 0) col = mix3(col, CFG[state].accent, r.activation * 0.4);

        const audioSize = state === 'listening' || state === 'speaking' ? sl * 0.9 : 0;
        const dotSize = (1.0 + d * 2.2) * r.scale + r.activation * 1.5 + audioSize;
        const alpha = Math.min(1, 0.2 + d * 0.75 + r.activation * 0.25);

        if (r.activation > 0.2) {
          ctx.shadowBlur = 4 * r.activation;
          ctx.shadowColor = `rgba(${col[0]},${col[1]},${col[2]},0.5)`;
        } else {
          ctx.shadowBlur = 0;
        }

        ctx.beginPath();
        ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${alpha})`;
        ctx.arc(r.px, r.py, Math.max(0.4, dotSize), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(Math.sin(t * 0.4) * 0.2);
      ctx.scale(1, Math.abs(Math.cos(rx)) * 0.5 + 0.12);
      ctx.beginPath();
      ctx.arc(0, 0, R * breathe * 1.02, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${cfg.glowHue},0.09)`;
      ctx.lineWidth = 1.1;
      ctx.stroke();
      ctx.restore();

      rafId = requestAnimationFrame(frame);
    };

    rafId = requestAnimationFrame(frame);
    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Fixed internal resolution (identical animation), responsive display size.
  return (
    <canvas ref={canvasRef} width={350} height={350} style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'block' }} />
  );
};

export default AnimationSpeack;
